import firestore from '@react-native-firebase/firestore';
import { Job, CreateJobData } from '../types/Job';
import { ErrorLogger } from '../utils/errorLogger';
import { mapJobDoc } from '../utils/mappers';
import { lastWeek, thisWeek } from '../utils/dateRange';

const JOBS_COLLECTION = 'jobs';

export class JobService {
  private static getJobsCollection() {
    return firestore().collection(JOBS_COLLECTION);
  }

  /**
   * Create a new job
   */
  static async createJob(createdBy: string, data: CreateJobData): Promise<Job> {
    try {
      const now = new Date();
      const jobData: Omit<Job, 'id'> = {
        status: data.status ?? 'pending',
        ...(data.customerPhone !== undefined && { customerPhone: data.customerPhone }),
        from: data.from,
        to: data.to,
        intakeAmount: data.intakeAmount,
        // Revenue is recognized only at completion: pending jobs carry 0. The
        // retro "record completed job" path passes the already-netted amount.
        amount: data.amount ?? 0,
        cargoTags: data.cargoTags,
        ...(data.description !== undefined && { description: data.description }),
        createdBy,
        ...(data.createdByName !== undefined && { createdByName: data.createdByName }),
        createdAt: now,
        updatedAt: now,
        ...(data.paymentType !== undefined && { paymentType: data.paymentType }),
        ...(data.serviceCost !== undefined && { serviceCost: data.serviceCost }),
        ...(data.assignedTruckId !== undefined && { assignedTruckId: data.assignedTruckId }),
        ...(data.assignedDriverName !== undefined && { assignedDriverName: data.assignedDriverName }),
        ...(data.completedBy !== undefined && { completedBy: data.completedBy }),
        ...(data.completedByName !== undefined && { completedByName: data.completedByName }),
        ...(data.completedAt !== undefined && { completedAt: data.completedAt }),
      };

      const docRef = await this.getJobsCollection().add(jobData);

      return { id: docRef.id, ...jobData };
    } catch (error) {
      ErrorLogger.logError(error, 'JobService.createJob');
      throw new Error('Failed to create job');
    }
  }

  /**
   * Subscribe to the job pool — all pending jobs
   */
  static subscribeToJobPool(
    onUpdate: (jobs: Job[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = this.getJobsCollection()
      .where('status', '==', 'pending')
      .onSnapshot(
        snapshot => {
          const jobs: Job[] = snapshot.docs.map(doc => mapJobDoc(doc));
          onUpdate(jobs);
        },
        error => {
          ErrorLogger.logError(error, 'JobService.subscribeToJobPool');
          onError?.(error);
          onUpdate([]);
        },
      );

    return unsubscribe;
  }

  /**
   * Subscribe to the single job currently assigned to a driver
   */
  static subscribeToDriverJob(
    driverId: string,
    onUpdate: (jobs: Job[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = this.getJobsCollection()
      .where('assignedDriverId', '==', driverId)
      .where('status', '==', 'assigned')
      .onSnapshot(
        snapshot => {
          const jobs: Job[] = snapshot.docs.map(doc => mapJobDoc(doc));
          onUpdate(jobs);
        },
        error => {
          ErrorLogger.logError(error, 'JobService.subscribeToDriverJob');
          onError?.(error);
          onUpdate([]);
        },
      );

    return unsubscribe;
  }

  /**
   * Subscribe to all jobs — supervisor view
   */
  static subscribeToAllJobs(
    onUpdate: (jobs: Job[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = this.getJobsCollection().onSnapshot(
      snapshot => {
        const jobs: Job[] = snapshot.docs.map(doc => mapJobDoc(doc));
        onUpdate(jobs);
      },
      error => {
        ErrorLogger.logError(error, 'JobService.subscribeToAllJobs');
        onError?.(error);
        onUpdate([]);
      },
    );

    return unsubscribe;
  }

  /**
   * Pick up a job — transition pending → assigned
   */
  static async pickUpJob(
    jobId: string,
    driverId: string,
    truckId: string,
    driverName: string,
  ): Promise<void> {
    try {
      const docRef = this.getJobsCollection().doc(jobId);

      // Run inside a transaction so two drivers can't both claim the same job:
      // the status is re-read and re-checked atomically, and if another driver
      // commits first, Firestore aborts and retries, seeing the new status.
      await firestore().runTransaction(async transaction => {
        const snap = await transaction.get(docRef);
        const data = snap.data() as Job | undefined;

        if (!data || data.status !== 'pending') {
          throw new Error('Job is not available for pickup');
        }

        transaction.update(docRef, {
          status: 'assigned',
          assignedDriverId: driverId,
          assignedTruckId: truckId,
          assignedDriverName: driverName,
          updatedAt: new Date(),
        });
      });
    } catch (error) {
      if (error instanceof Error && error.message === 'Job is not available for pickup') {
        throw error;
      }
      ErrorLogger.logError(error, 'JobService.pickUpJob');
      throw new Error('Failed to pick up job');
    }
  }

  /**
   * Release a job — transition assigned → pending
   */
  static async releaseJob(jobId: string): Promise<void> {
    try {
      const docRef = this.getJobsCollection().doc(jobId);
      const snap = await docRef.get();
      const data = snap.data() as Job;

      if (data.status !== 'assigned') {
        throw new Error('Job is not assigned');
      }

      await docRef.update({
        status: 'pending',
        assignedDriverId: null,
        assignedTruckId: null,
        assignedDriverName: null,
        updatedAt: new Date(),
      });
    } catch (error) {
      if (error instanceof Error && error.message === 'Job is not assigned') {
        throw error;
      }
      ErrorLogger.logError(error, 'JobService.releaseJob');
      throw new Error('Failed to release job');
    }
  }

  /**
   * Complete a job — transition assigned → completed
   */
  static async completeJob(
    jobId: string,
    paymentType: 'cash' | 'debt',
    serviceCost: number,
    completedBy: string,
    completedByName: string,
  ): Promise<void> {
    try {
      const docRef = this.getJobsCollection().doc(jobId);
      const snap = await docRef.get();
      const data = snap.data() as Job | undefined;

      if (!data || data.status !== 'assigned') {
        throw new Error('Job is not assigned');
      }

      // Recognize revenue now: net the negotiated intake price against the cost
      // of delivering the service. Derived from the stored doc, not a passed
      // value, so the revenue number is authoritative.
      const amount = (data.intakeAmount ?? 0) - serviceCost;

      const now = new Date();
      await docRef.update({
        status: 'completed',
        paymentType,
        serviceCost,
        amount,
        completedBy,
        completedByName,
        completedAt: now,
        updatedAt: now,
      });
    } catch (error) {
      if (error instanceof Error && error.message === 'Job is not assigned') {
        throw error;
      }
      ErrorLogger.logError(error, 'JobService.completeJob');
      throw new Error('Failed to complete job');
    }
  }

  /**
   * Delete a job permanently
   */
  static async deleteJob(jobId: string): Promise<void> {
    try {
      await this.getJobsCollection().doc(jobId).delete();
    } catch (error) {
      ErrorLogger.logError(error, 'JobService.deleteJob');
      throw new Error('Failed to delete job');
    }
  }

  static async getCompletedJobsByDriver(driverId: string): Promise<Job[]> {
    try {
      const snapshot = await this.getJobsCollection()
        .where('status', '==', 'completed')
        .where('assignedDriverId', '==', driverId)
        .get();
      return snapshot.docs
        .map(doc => mapJobDoc(doc))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      ErrorLogger.logError(error, 'JobService.getCompletedJobsByDriver');
      return [];
    }
  }

  static async getCompletedJobsThisWeek(): Promise<Job[]> {
    try {
      const { start, end } = thisWeek();
      const snapshot = await this.getJobsCollection()
        .where('status', '==', 'completed')
        .get();
      return snapshot.docs
        .map(doc => mapJobDoc(doc))
        .filter(job => job.createdAt >= start && job.createdAt <= end)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      ErrorLogger.logError(error, 'JobService.getCompletedJobsThisWeek');
      return [];
    }
  }

  static async getAllCompletedJobs(): Promise<Job[]> {
    try {
      const snapshot = await this.getJobsCollection()
        .where('status', '==', 'completed')
        .get();
      return snapshot.docs
        .map(doc => mapJobDoc(doc))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      ErrorLogger.logError(error, 'JobService.getAllCompletedJobs');
      return [];
    }
  }

  static async getCompletedJobsLastWeek(): Promise<Job[]> {
    try {
      const { start, end } = lastWeek();
      const snapshot = await this.getJobsCollection()
        .where('status', '==', 'completed')
        .get();
      return snapshot.docs
        .map(doc => mapJobDoc(doc))
        .filter(job => job.createdAt >= start && job.createdAt <= end);
    } catch (error) {
      ErrorLogger.logError(error, 'JobService.getCompletedJobsLastWeek');
      return [];
    }
  }
}

export default JobService;
